import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { CrmConfig } from './crmUtils';

interface CrmHeaderProps {
  crm?: CrmConfig;
  isLight: boolean;
  showHeaderMobile: boolean;
  setShowHeaderMobile: (show: boolean) => void;
  setShowLargeQr: (show: boolean) => void;
  pushStatus: 'granted' | 'default' | 'denied' | 'unsupported';
  subscribeToWebPush: (silent?: boolean) => Promise<boolean>;
  setIsDrawerOpen: (open: boolean) => void;
  setSettingsTab: (tab: 'personal' | 'general' | 'security' | 'users') => void;
  activeTab: 'logs' | 'chat' | 'cdr' | 'voicemails';
  setActiveTab: (tab: 'logs' | 'chat' | 'cdr' | 'voicemails') => void;
  hotCallsCount: number;
  unreadMessages: number;
  setAddressBookModalOpen: (open: boolean) => void;
}

export function CrmHeader({
  crm,
  isLight,
  showHeaderMobile,
  setShowHeaderMobile,
  setShowLargeQr,
  pushStatus,
  subscribeToWebPush,
  setIsDrawerOpen,
  setSettingsTab,
  activeTab,
  setActiveTab,
  hotCallsCount,
  unreadMessages,
  setAddressBookModalOpen
}: CrmHeaderProps) {
  return (
    <header
      className={`crm-dashboard-header ${showHeaderMobile ? 'expanded' : 'collapsed'}`}
      style={{
        textAlign: 'center',
        borderBottom: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
        paddingBottom: '1.5rem',
        position: 'relative'
      }}
    >
      <div className="crm-header-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
        <h1
          style={{
            margin: '0',
            fontSize: '2rem',
            background: isLight ? 'none' : undefined,
            WebkitTextFillColor: isLight ? '#09090b' : undefined,
            WebkitBackgroundClip: isLight ? 'unset' : undefined
          }}
        >
          {crm?.name || 'Live CRM Viewer'}
        </h1>
        <div
          onClick={() => setShowLargeQr(true)}
          style={{
            cursor: 'pointer',
            padding: '4px',
            background: 'white',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            flexShrink: 0,
            marginTop: '4px'
          }}
          title="Show QR Code"
        >
          <QRCodeSVG value={typeof window !== 'undefined' ? window.location.href : ''} size={24} level="L" />
        </div>
        {typeof window !== 'undefined' && 'Notification' in window && (
          <button
            onClick={() => {
              if (pushStatus !== 'granted') {
                subscribeToWebPush(false);
              } else {
                setIsDrawerOpen(true);
                setSettingsTab('personal');
              }
            }}
            style={{
              position: 'relative',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0.2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: pushStatus === 'granted' ? (isLight ? '#16a34a' : '#22c55e') : (isLight ? '#6b7280' : '#a1a1aa'),
              transition: 'color 0.2s',
              marginTop: '4px'
            }}
            title={pushStatus === 'granted' ? 'Web Push Active - Click to Manage in Settings' : 'Enable Browser Push Notifications'}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>
            {pushStatus !== 'granted' && (
              <span style={{ position: 'absolute', top: '2px', right: '2px', width: '8px', height: '8px', background: '#f59e0b', borderRadius: '50%', border: '2px solid ' + (isLight ? 'white' : '#09090b') }} />
            )}
          </button>
        )}
      </div>

      {crm?.enableChatClient && (
        <div className="crm-header-tabs" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem' }}>
          {crm?.enableCurrentCallers !== false && (
            <button
              onClick={() => { setActiveTab('logs'); setShowHeaderMobile(false); }}
              style={{
                position: 'relative',
                padding: '0.5rem 1.5rem',
                borderRadius: '20px',
                background: activeTab === 'logs' ? (isLight ? '#09090b' : 'white') : 'transparent',
                color: activeTab === 'logs' ? (isLight ? 'white' : '#09090b') : (isLight ? '#6b7280' : '#a1a1aa'),
                border: activeTab === 'logs' ? 'none' : (isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)'),
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Callers
              {hotCallsCount > 0 && <span style={{ marginLeft: '0.5rem', background: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold' }}>{hotCallsCount}</span>}
            </button>
          )}
          <button
            onClick={() => { setActiveTab('chat'); setShowHeaderMobile(false); }}
            style={{
              position: 'relative',
              padding: '0.5rem 1.5rem',
              borderRadius: '20px',
              background: activeTab === 'chat' ? (isLight ? '#09090b' : 'white') : 'transparent',
              color: activeTab === 'chat' ? (isLight ? 'white' : '#09090b') : (isLight ? '#6b7280' : '#a1a1aa'),
              border: activeTab === 'chat' ? 'none' : (isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)'),
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Messages
            {unreadMessages > 0 && <span style={{ marginLeft: '0.5rem', background: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold' }}>{unreadMessages}</span>}
          </button>

          {crm?.enableVoicemails && (
            <button
              onClick={() => setActiveTab('voicemails')}
              style={{
                position: 'relative',
                padding: '0.5rem 1.5rem',
                borderRadius: '20px',
                background: activeTab === 'voicemails' ? (isLight ? '#09090b' : 'white') : 'transparent',
                color: activeTab === 'voicemails' ? (isLight ? 'white' : '#09090b') : (isLight ? '#6b7280' : '#a1a1aa'),
                border: activeTab === 'voicemails' ? 'none' : (isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)'),
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Voicemails
            </button>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {crm?.enableCdr && (
              <button
                onClick={() => { setActiveTab('cdr'); setShowHeaderMobile(false); }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.2rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: activeTab === 'cdr' ? (isLight ? '#09090b' : 'white') : (isLight ? '#6b7280' : '#a1a1aa'),
                  transition: 'color 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.color = isLight ? '#09090b' : 'white'}
                onMouseLeave={e => e.currentTarget.style.color = activeTab === 'cdr' ? (isLight ? '#09090b' : 'white') : (isLight ? '#6b7280' : '#a1a1aa')}
                title="Call Detail Records"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path></svg>
              </button>
            )}
            {crm?.freepbxServerId && (
              <button
                onClick={() => setAddressBookModalOpen(true)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.2rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isLight ? '#6b7280' : '#a1a1aa',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.color = isLight ? '#09090b' : 'white'}
                onMouseLeave={e => e.currentTarget.style.color = isLight ? '#6b7280' : '#a1a1aa'}
                title="Open Address Book"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              </button>
            )}
            <button
              onClick={() => setIsDrawerOpen(true)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '0.2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isLight ? '#6b7280' : '#a1a1aa',
                transition: 'color 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.color = isLight ? '#09090b' : 'white'}
              onMouseLeave={e => e.currentTarget.style.color = isLight ? '#6b7280' : '#a1a1aa'}
              title="Open Settings"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
